/**
 * Sawyer #1296 — "Add this to the owner's notes?", the one prompt every
 * accept and check-in surface shows AFTER its action succeeded.
 *
 *   "Add this to the owner's notes?"
 *   > Juniper is on a new food, half scoop at lunch
 *   [Not now]  [Add to owner's notes]
 *
 * Ordering is the safety argument (the run-card prompt's, #716's): the
 * prompt is offered only after the server accepted the reservation or the
 * check-in, so dismissing it, a failed add, losing the network or closing
 * the tab all leave the reservation accepted / the dog checked in. Nothing
 * here can undo either.
 *
 * Who decides whether to ask: `ownerNoteCandidates` (owner-notes-prompt.ts),
 * from facts the surface already holds. No fetch happens before the prompt.
 *
 * Bulk: a batch accept or "Check in all" offers ONE dialog listing each note
 * with its own "Add" button; anything not added stays on its booking. A later
 * offer while the dialog is open joins the list rather than stacking.
 *
 * Never modal-on-modal: while `blocked` (the run-card prompt, an arrival
 * question, a family picker…) the prompt waits, state kept, and shows the
 * moment the other dialog closes.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type JSX } from 'react';
import { readSession } from '../../lib/api.js';
import { addBookingNoteToOwnerNotes } from '../../lib/client-notes-api.js';
import { effectiveRolesOf, rolesHavePermission } from '../../lib/rbac-matrix.js';
import { joinDogNames } from '../dashboard/household-checkin.js';
import './booking-notes.css';
import {
  CLIENT_NOTES_WRITE_PERMISSION,
  mergeCandidates,
  ownerNoteCandidates,
  ownerNoteErrorCopy,
  type BookingNoteFacts,
  type OwnerNoteCandidate,
} from './owner-notes-prompt.js';

export type OwnerNoteRowState =
  | { readonly kind: 'asking' }
  | { readonly kind: 'adding' }
  | { readonly kind: 'added' }
  | { readonly kind: 'failed'; readonly message: string };

export interface OwnerNotesPromptItem {
  readonly candidate: OwnerNoteCandidate;
  readonly state: OwnerNoteRowState;
}

export interface UseOwnerNotesPromptOptions {
  readonly apiUrl: string;
  /** Another dialog is open: wait (keep state), show when it closes. */
  readonly blocked?: boolean;
  /** Called after a note was kept, so the page can refetch its flags. */
  readonly onAdded?: () => void;
}

export interface OwnerNotesPrompt {
  /** Call AFTER an accept / check-in succeeded, with the rows that went through. */
  readonly offer: (rows: ReadonlyArray<BookingNoteFacts>) => void;
  /** Render once, anywhere in the page. Null when nothing to ask (or blocked). */
  readonly element: JSX.Element | null;
}

export function useOwnerNotesPrompt({ apiUrl, blocked = false, onAdded }: UseOwnerNotesPromptOptions): OwnerNotesPrompt {
  const [items, setItems] = useState<OwnerNotesPromptItem[]>([]);
  // Notes kept from this page: the server's flag is only as fresh as the
  // last refetch, so a note added a minute ago must not be asked about again.
  const addedHere = useRef<Set<string>>(new Set());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const onAddedRef = useRef(onAdded);
  onAddedRef.current = onAdded;

  const offer = useCallback((rows: ReadonlyArray<BookingNoteFacts>) => {
    const { staff } = readSession();
    const candidates = ownerNoteCandidates(rows, {
      canWriteClientNotes: rolesHavePermission(effectiveRolesOf(staff), CLIENT_NOTES_WRITE_PERMISSION),
      addedHere: addedHere.current,
    });
    if (candidates.length === 0) return;
    setItems((prev) => {
      const merged = mergeCandidates(
        prev.map((i) => i.candidate),
        candidates,
      );
      const byId = new Map(prev.map((i) => [i.candidate.bookingId, i] as const));
      return merged.map((c) => byId.get(c.bookingId) ?? { candidate: c, state: { kind: 'asking' } });
    });
  }, []);

  const setRow = useCallback((bookingId: string, state: OwnerNoteRowState) => {
    setItems((prev) => prev.map((i) => (i.candidate.bookingId === bookingId ? { ...i, state } : i)));
  }, []);

  const add = useCallback(
    async (bookingId: string, closeOnSuccess: boolean) => {
      const item = items.find((i) => i.candidate.bookingId === bookingId);
      if (!item || item.state.kind === 'adding' || item.state.kind === 'added') return;
      const { token } = readSession();
      setRow(bookingId, { kind: 'adding' });
      try {
        if (!token) throw new Error('unauthorized');
        await addBookingNoteToOwnerNotes(apiUrl, token, item.candidate.customerId, bookingId);
        addedHere.current.add(bookingId);
        if (!mounted.current) return;
        if (closeOnSuccess) {
          setItems((prev) => prev.filter((i) => i.candidate.bookingId !== bookingId));
        } else {
          setRow(bookingId, { kind: 'added' });
        }
        onAddedRef.current?.();
      } catch (err) {
        if (!mounted.current) return;
        const e = err as { code?: string; status?: number } | null;
        setRow(bookingId, { kind: 'failed', message: ownerNoteErrorCopy(e?.code ?? null, e?.status ?? null) });
      }
    },
    [apiUrl, items, setRow],
  );

  const close = useCallback(() => setItems([]), []);

  const element = useMemo(() => {
    if (blocked || items.length === 0) return null;
    return (
      <OwnerNotesPromptDialog
        items={items}
        onAdd={(bookingId) => void add(bookingId, items.length === 1)}
        onClose={close}
      />
    );
  }, [add, blocked, close, items]);

  return { offer, element };
}

export interface OwnerNotesPromptDialogProps {
  readonly items: ReadonlyArray<OwnerNotesPromptItem>;
  readonly onAdd: (bookingId: string) => void;
  /** "Not now" / "Done" / Escape: whatever was not added stays on its booking. */
  readonly onClose: () => void;
}

function whoseNote(c: OwnerNoteCandidate): string {
  const dogs = joinDogNames(c.dogNames);
  if (dogs && c.ownerName) return `${dogs} · ${c.ownerName}`;
  return dogs || c.ownerName || 'This booking';
}

/** The prompt itself. Presentational; exported for the component test. */
export function OwnerNotesPromptDialog({ items, onAdd, onClose }: OwnerNotesPromptDialogProps): JSX.Element {
  const titleId = useId();
  const primaryRef = useRef<HTMLButtonElement>(null);
  const one = items.length === 1;
  useEffect(() => {
    // The common answer for a single note is one tap (or Enter).
    if (one) primaryRef.current?.focus();
  }, [one]);
  const anyAdded = items.some((i) => i.state.kind === 'added');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="owner-notes-prompt"
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose();
      }}
    >
      <div className="ferro-blocking-dialog__panel ferro-owner-notes-prompt">
        <h2 id={titleId}>{one ? "Add this to the owner's notes?" : `Add these ${items.length} notes to the owners' notes?`}</h2>
        <p className="ferro-owner-notes-prompt__hint">
          {one
            ? "Owner's notes show on every one of their dogs. Not now keeps it on this booking only."
            : 'Add the ones worth keeping. Anything you skip stays on its booking only.'}
        </p>
        {one ? (
          <SingleNote item={items[0]!} />
        ) : (
          <ul className="ferro-sibling-prompt__list" data-testid="owner-notes-prompt-list">
            {items.map((item) => (
              <li key={item.candidate.bookingId} className="ferro-sibling-prompt__item ferro-owner-notes-prompt__item">
                <p className="ferro-sibling-prompt__dog">{whoseNote(item.candidate)}</p>
                <blockquote
                  className="ferro-booking-note ferro-owner-notes-prompt__quote"
                  data-testid={`owner-notes-prompt-note-${item.candidate.bookingId}`}
                >
                  <p className="ferro-booking-note__text">{item.candidate.note}</p>
                </blockquote>
                <RowStatus item={item} />
                {item.state.kind === 'added' ? null : (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost ferro-owner-notes-prompt__add"
                    disabled={item.state.kind === 'adding'}
                    onClick={() => onAdd(item.candidate.bookingId)}
                    data-testid={`owner-notes-prompt-add-${item.candidate.bookingId}`}
                    aria-label={`Add ${whoseNote(item.candidate)} note to the owner's notes`}
                  >
                    {item.state.kind === 'adding' ? 'Adding…' : item.state.kind === 'failed' ? 'Try again' : "Add to owner's notes"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="ferro-blocking-dialog__actions">
          {one ? (
            <>
              <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onClose} data-testid="owner-notes-prompt-skip">
                Not now
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--primary"
                ref={primaryRef}
                disabled={items[0]!.state.kind === 'adding'}
                onClick={() => onAdd(items[0]!.candidate.bookingId)}
                data-testid="owner-notes-prompt-add"
              >
                {items[0]!.state.kind === 'adding'
                  ? 'Adding…'
                  : items[0]!.state.kind === 'failed'
                    ? 'Try again'
                    : "Add to owner's notes"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={anyAdded ? 'ferro-btn ferro-btn--primary' : 'ferro-btn ferro-btn--ghost'}
              onClick={onClose}
              data-testid="owner-notes-prompt-done"
            >
              {anyAdded ? 'Done' : 'Not now'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SingleNote({ item }: { readonly item: OwnerNotesPromptItem }): JSX.Element {
  return (
    <>
      <p className="ferro-sibling-prompt__dog">{whoseNote(item.candidate)}</p>
      <blockquote
        className="ferro-booking-note ferro-owner-notes-prompt__quote"
        data-testid={`owner-notes-prompt-note-${item.candidate.bookingId}`}
      >
        <p className="ferro-booking-note__text">{item.candidate.note}</p>
      </blockquote>
      <RowStatus item={item} />
    </>
  );
}

function RowStatus({ item }: { readonly item: OwnerNotesPromptItem }): JSX.Element | null {
  if (item.state.kind === 'failed') {
    return (
      <p className="ferro-owner-notes-prompt__error" role="alert" data-testid={`owner-notes-prompt-error-${item.candidate.bookingId}`}>
        {item.state.message}
      </p>
    );
  }
  if (item.state.kind === 'added') {
    return (
      <p className="ferro-owner-notes-prompt__added" role="status" data-testid={`owner-notes-prompt-added-${item.candidate.bookingId}`}>
        Added to the owner's notes, pinned.
      </p>
    );
  }
  return null;
}
